import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Formik, Field, FieldArray, getIn } from "formik";
import { Grid } from "@mui/material";
import { InputField } from "../../../../components/Form/InputField";
import { DishFormValues, PropsAddAndEditDish } from "../../Types/BuyerConfigstype";
import { memo, useCallback, useEffect, useState } from "react";
import ImageUpload from "../../../../components/Form/ImageUpload";
import SelectFieldWithSearch from "../../../../components/Form/SelectFieldWithSearch";
import { foodAndBeverageTypeOptions } from "../../../Stores/Utils/mockData";
import { createDish, getDishById, updateDish } from "../../../../service/BuyerConfiguration/DishManagement/dishService";
import { notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";
import { notifyError } from "../../../../components/ToastContainer/Toast/Toast";
import { dishValidationSchema } from "../../Validation/buyerConfigValidation";
import { CustomSwitch } from "../../../../components/Form/CustomSwitch";
import copyFromObj from "copyfromobj";

const initValues: DishFormValues = {
  name: "",
  food_type: "",
  icon: null,
  status: false,
  schedule_enabled: false,
  sessions: [],
};

const DAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];

const toTimeInput = (hhmm?: number) => {
  const value = Number(hhmm ?? 0);
  const h = Math.floor(value / 100)
    .toString()
    .padStart(2, "0");
  const m = (value % 100).toString().padStart(2, "0");
  return `${h}:${m}`;
};

const normalizeHHMM = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value !== "string") return 0;
  const raw = value.trim();
  if (!raw) return 0;
  if (/^\d{1,4}$/.test(raw)) {
    return parseInt(raw, 10);
  }
  const withSeconds = raw.match(
    /^(\d{1,2}):(\d{2})(?::\d{1,2}(?:\.\d+)?)?$/,
  );
  if (withSeconds) {
    return Number(withSeconds[1]) * 100 + Number(withSeconds[2]);
  }
  const twelveHour = raw.match(
    /^(\d{1,2}):(\d{2})(?::\d{1,2}(?:\.\d+)?)?\s*(AM|PM)$/i,
  );
  if (!twelveHour) return 0;
  let hour = Number(twelveHour[1]);
  const minute = Number(twelveHour[2]);
  const meridiem = twelveHour[3].toUpperCase();
  if (meridiem === "AM") {
    if (hour === 12) hour = 0;
  } else if (hour !== 12) {
    hour += 12;
  }
  return hour * 100 + minute;
};

const fromTimeInput = (value: string) => {
  if (!value?.trim()) return 0;
  return normalizeHHMM(value);
};

/** Build FormData for multipart create/update so the API receives fields and file correctly. */
function buildDishFormData(payload: DishFormValues): FormData {
  const formData = new FormData();
  formData.append("name", payload.name);
  formData.append("food_type", payload.food_type);
  formData.append("status", String(Boolean(payload.status)));
  formData.append("schedule_enabled", String(Boolean(payload.schedule_enabled)));
  if (Array.isArray(payload.sessions)) {
    const normalizeTimeField = (session: any, keys: string[]) => {
      for (const key of keys) {
        if (session?.[key] !== undefined && session?.[key] !== null && session?.[key] !== "") {
          return normalizeHHMM(session[key]);
        }
      }
      return 0;
    };
    const normalizedSessions = payload.sessions.map((session) => ({
      ...session,
      day_from: Number(session.day_from),
      day_to: Number(session.day_to),
      start_hhmm: normalizeTimeField(session as any, [
        "start_hhmm",
        "start_time",
        "startTime",
        "from",
      ]),
      end_hhmm: normalizeTimeField(session as any, [
        "end_hhmm",
        "end_time",
        "endTime",
        "to",
      ]),
      status: true,
    }));
    formData.append("sessions", JSON.stringify(normalizedSessions));
  }
  const iconFile = payload?.icon?.[0]?.originFileObj ?? payload?.icon;
  if (iconFile instanceof File) {
    formData.append("icon", iconFile);
  }
  return formData;
}

function touchAllDishFields(values: DishFormValues) {
  return {
    name: true,
    food_type: true,
    icon: true,
    status: true,
    schedule_enabled: true,
    sessions: (values.sessions || []).map(() => ({
      day_from: true,
      day_to: true,
      start_hhmm: true,
      end_hhmm: true,
      label: true,
      status: true,
    })),
  };
}

function summarizeFormikErrors(errors: Record<string, unknown>): string {
  const sessions = errors.sessions;
  if (sessions) {
    if (typeof sessions === "string") return sessions;
    if (Array.isArray(sessions)) {
      for (const row of sessions) {
        if (row && typeof row === "object") {
          for (const v of Object.values(row as Record<string, unknown>)) {
            if (typeof v === "string" && v) return v;
          }
        }
      }
    }
    return "Fix session day and time values before saving.";
  }
  for (const key of ["name", "food_type", "icon", "status"] as const) {
    const v = errors[key];
    if (typeof v === "string" && v) return v;
  }
  return "Please fix the highlighted fields before saving.";
}

const AddAndEditDish: React.FC<PropsAddAndEditDish> = ({ fetchDishes, dialogMode, selectedRowId, setOpenModal, setDialogMode }) => {
  const [initialValues, setInitialValues] = useState<DishFormValues>(initValues);
  const [submitUiAttempt, setSubmitUiAttempt] = useState(0);


  const fetchDishById = async () => {
    try {
      const response = await getDishById(selectedRowId);
      if (response.statusCode === 200 && response.data) {
        const data = copyFromObj(initValues, response.data);
        data.schedule_enabled = Boolean(response.data?.schedule_enabled);
        data.sessions = Array.isArray(response.data?.sessions)
          ? response.data.sessions.map((s: any) => ({
              day_from: Number(s.day_from ?? 1),
              day_to: Number(s.day_to ?? 7),
              start_hhmm: Number(s.start_hhmm ?? 700),
              end_hhmm: Number(s.end_hhmm ?? 1130),
              label: s.label ?? "",
              status: s.status !== false,
            }))
          : [];
        setInitialValues(data);
      } else {
        setInitialValues(initValues);
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || "Something went wrong!");
    }
  };

  useEffect(() => {
    setSubmitUiAttempt(0);
    if (dialogMode === "EditDish" && selectedRowId) {
      fetchDishById();
    } else {
      setInitialValues(initValues);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowId, dialogMode]);

  const handleCreateDish = useCallback(async (postPayload: DishFormValues) => {
    try {
      const formData = buildDishFormData(postPayload);
      const response = await createDish(formData);
      const ok =
        response?.statusCode === 200 ||
        response?.statusCode === 201 ||
        response?.success === true;
      if (ok) {
        notifySuccess(
          response?.message || response?.data?.message || "Dish created successfully"
        );
        fetchDishes?.();
        setOpenModal(false);
        setDialogMode(null);
      } else {
        notifyError(response?.message || "Failed to create dish.");
      }
    } catch (error: any) {
      notifyError(
        error?.response?.data?.message || error?.message || "Something went wrong!"
      );
    }
  }, [fetchDishes, setOpenModal, setDialogMode]);

  const handleUpdateDish = useCallback(async (postPayload: DishFormValues) => {
    try {
      const formData = buildDishFormData(postPayload);
      const response = await updateDish(selectedRowId, formData);
      const ok =
        response?.statusCode === 200 ||
        response?.statusCode === 201 ||
        response?.success === true;
      if (ok) {
        notifySuccess(
          response?.message || response?.data?.message || "Dish updated successfully"
        );
        fetchDishes?.();
        setOpenModal(false);
        setDialogMode(null);
      } else {
        notifyError(response?.message || "Failed to update dish.");
      }
    } catch (error: any) {
      notifyError(
        error?.response?.data?.message || error?.message || "Something went wrong!"
      );
    }
  }, [selectedRowId, fetchDishes, setOpenModal, setDialogMode]);

  const handleSubmit = async (values: DishFormValues) => {
    const postPayload: DishFormValues = {
      ...values,
      icon: values?.icon ?? initialValues?.icon ?? null,
    };
    if (dialogMode === "AddDish" && !selectedRowId) {
      await handleCreateDish(postPayload);
    } else if (dialogMode === "EditDish" && selectedRowId) {
      await handleUpdateDish(postPayload);
    }
  };
  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: { sm: "100%", md: "1700px" },
          height: "100%",
        }}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={dishValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            errors,
            validateForm,
            setTouched,
            setSubmitting,
            isSubmitting,
          }) => {
            const sessionsError =
              typeof errors.sessions === "string" ? errors.sessions : undefined;
            const showFieldErrors = submitUiAttempt > 0;

            const attemptSubmit = async () => {
              setSubmitUiAttempt((n) => n + 1);
              const formErrors = await validateForm();
              if (Object.keys(formErrors).length > 0) {
                setTouched(touchAllDishFields(values), true);
                notifyError(summarizeFormikErrors(formErrors as Record<string, unknown>));
                return;
              }
              await setSubmitting(true);
              try {
                await handleSubmit(values);
              } finally {
                await setSubmitting(false);
              }
            };

            return (
              <form
                noValidate
                onSubmit={async (e) => {
                  e.preventDefault();
                  await attemptSubmit();
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Field
                    name={"icon"}
                    component={ImageUpload}
                    outSideTitle="Upload Dish Icon"
                    width="100%"
                    isRequired
                  />
                  <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                      <InputField
                        label={"Dish Name"}
                        name={"name"}
                        isRequired
                        placeholder={"Enter Dish Name"}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectFieldWithSearch
                        label={"Food Type"}
                        name={"food_type"}
                        value={values?.food_type}
                        isRequired
                        size="large"
                        placeholder={"Select Food Type"}
                        height="50px"
                        options={foodAndBeverageTypeOptions.map((type) => ({
                          value: type.value,
                          label: type.label,
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CustomSwitch
                        label={"Status"}
                        name={"status"}
                        checked={Boolean(values?.status)}
                        onChange={(checked) => {
                          handleChange({
                            target: { name: "status", value: checked },
                          } as any);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CustomSwitch
                        label={"Enable Schedule"}
                        name={"schedule_enabled"}
                        checked={Boolean(values?.schedule_enabled)}
                        onChange={(checked) => {
                          handleChange({
                            target: { name: "schedule_enabled", value: checked },
                          } as any);
                        }}
                      />
                    </Grid>

                  </Grid>
                </Box>
                {values?.schedule_enabled && (
                  <Box sx={{ mt: 2 }}>
                    <FieldArray name="sessions">
                      {({ push, remove }) => (
                        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                            End day must be on/after start day; end time must be after start time, except overnight times are allowed for short ranges.
                          </Typography>
                          {showFieldErrors && sessionsError && (
                            <Alert severity="error" sx={{ mb: 1.5 }}>
                              {sessionsError}
                            </Alert>
                          )}
                          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}>
                            <Chip
                              label="All Days"
                              onClick={() => {
                                const sessions = values.sessions || [];
                                sessions.forEach((_, idx) => {
                                  handleChange({ target: { name: `sessions.${idx}.day_from`, value: 1 } } as any);
                                  handleChange({ target: { name: `sessions.${idx}.day_to`, value: 7 } } as any);
                                });
                              }}
                            />
                            <Chip
                              label="Weekdays"
                              onClick={() => {
                                const sessions = values.sessions || [];
                                sessions.forEach((_, idx) => {
                                  handleChange({ target: { name: `sessions.${idx}.day_from`, value: 1 } } as any);
                                  handleChange({ target: { name: `sessions.${idx}.day_to`, value: 5 } } as any);
                                });
                              }}
                            />
                            <Chip
                              label="Weekends"
                              onClick={() => {
                                const sessions = values.sessions || [];
                                sessions.forEach((_, idx) => {
                                  handleChange({ target: { name: `sessions.${idx}.day_from`, value: 6 } } as any);
                                  handleChange({ target: { name: `sessions.${idx}.day_to`, value: 7 } } as any);
                                });
                              }}
                            />
                          </Stack>

                          {(values.sessions || []).map((session, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                border: "1px solid #ececec",
                                borderRadius: 1.5,
                                p: 1.5,
                                mb: 1.5,
                              }}
                            >
                              <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    placeholder="Day From"
                                    value={session?.day_from ?? 1}
                                    error={
                                      showFieldErrors &&
                                      Boolean(getIn(errors, `sessions.${idx}.day_from`))
                                    }
                                    helperText={
                                      showFieldErrors
                                        ? (getIn(errors, `sessions.${idx}.day_from`) as string)
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleChange({
                                        target: { name: `sessions.${idx}.day_from`, value: Number(e.target.value) },
                                      } as any)
                                    }
                                  >
                                    {DAY_OPTIONS.map((d) => (
                                      <MenuItem key={d.value} value={d.value}>
                                        {d.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    placeholder="Day To"
                                    value={session?.day_to ?? 7}
                                    error={
                                      showFieldErrors &&
                                      Boolean(getIn(errors, `sessions.${idx}.day_to`))
                                    }
                                    helperText={
                                      showFieldErrors
                                        ? (getIn(errors, `sessions.${idx}.day_to`) as string)
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleChange({
                                        target: { name: `sessions.${idx}.day_to`, value: Number(e.target.value) },
                                      } as any)
                                    }
                                  >
                                    {DAY_OPTIONS.map((d) => (
                                      <MenuItem key={d.value} value={d.value}>
                                        {d.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    type="time"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={toTimeInput(session?.start_hhmm)}
                                    error={
                                      showFieldErrors &&
                                      Boolean(getIn(errors, `sessions.${idx}.start_hhmm`))
                                    }
                                    helperText={
                                      showFieldErrors
                                        ? (getIn(errors, `sessions.${idx}.start_hhmm`) as string)
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleChange({
                                        target: {
                                          name: `sessions.${idx}.start_hhmm`,
                                          value: fromTimeInput(e.target.value),
                                        },
                                      } as any)
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    type="time"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={toTimeInput(session?.end_hhmm)}
                                    error={
                                      showFieldErrors &&
                                      Boolean(getIn(errors, `sessions.${idx}.end_hhmm`))
                                    }
                                    helperText={
                                      showFieldErrors
                                        ? (getIn(errors, `sessions.${idx}.end_hhmm`) as string)
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleChange({
                                        target: {
                                          name: `sessions.${idx}.end_hhmm`,
                                          value: fromTimeInput(e.target.value),
                                        },
                                      } as any)
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Breakfast"
                                    value={session?.label || ""}
                                    onChange={(e) =>
                                      handleChange({
                                        target: { name: `sessions.${idx}.label`, value: e.target.value },
                                      } as any)
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <Stack
                                    direction="column"
                                    spacing={1}
                                    alignItems="flex-start"
                                    justifyContent="center"
                                    sx={{ width: "100%" }}
                                  >
                                    <Button
                                      color="error"
                                      variant="outlined"
                                      size="small"
                                      onClick={() => remove(idx)}
                                      sx={{ minWidth: 90, whiteSpace: "nowrap" }}
                                    >
                                      Remove
                                    </Button>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}

                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() =>
                              push({
                                day_from: 1,
                                day_to: 7,
                                start_hhmm: 700,
                                end_hhmm: 1130,
                                label: "Breakfast",
                                status: true,
                              })
                            }
                          >
                            Add Session
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>
                )}
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}
                >
                  <Button type="button" variant="outlined" color="primary" onClick={() => {
                    setOpenModal(false);
                    setDialogMode(null);
                  }}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {dialogMode === "EditDish" ? "Update Dish" : "Create Dish"}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};

export default memo(AddAndEditDish);
